import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import DeleteIcon from '@mui/icons-material/Delete';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Avatar from '../../../../../components/avatar/Avatar';
import ReviewerUsersPanel from './ReviewerUsersPanel/ReviewerUsersPanel';
import ReviewersPanelHeader from './ReviewersPanelHeader';
import Chip from '@mui/material/Chip';
import TextNotAssigned from '../../../../../components/text/TextNotAssigned';
import { useState, useEffect } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { getUsers } from '../../../../../services/user/user.service';
import { updateFilter } from '../../../../../store/actions/filter.actions';
import { updateAssessmentsBuilderClass } from '../../../../../store/actions/dataEntryAssessmentBuilderActions';
import './ReviewersPanel.css';

const getInitialClasses = (classesList) => {
  return classesList
    ?.filter((item) => item?.id !== 'all')
    .map((item) => {
      const reviewers =
        item?.hasOwnProperty('reviewers') && item.reviewers !== null ? item.reviewers : item.teachers;
      return { ...item, display: true, reviewers: reviewers || [] };
    });
};

const ReviewersPanel = ({ classesList = [], isConfirming = false, setIsConfirming }) => {
  const list = getInitialClasses(classesList);
  const [isMounted, setIsMounted] = useState(false);
  const [classes, setClasses] = useState(list);
  const [classInputSearch, setClassInputSearch] = useState('');
  const [userInputSearch, setUserInputSearch] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const filter = useSelector((state) => state?.filter?.assessmentReviewers?.filter || {});
  const open = useSelector((state) => state?.filter?.assessmentReviewers?.open || false);
  const userRoles = useSelector((state) => state?.session?.user?.roles || []);
  const dispatch = useDispatch();

  const applyClassDispersedSearch = (searchTerm = '', originalList = []) => {
    const term = searchTerm.toLowerCase();

    const updatedList = originalList?.map((item) => {
      const searchPool = item?.reviewers || [];

      // Check if name matches
      const nameMatch = searchPool?.some((person) => {
        const fullName = `${person.firstName} ${person.lastName}`.toLowerCase();
        return fullName.includes(term);
      });

      // Check if class code or label matches
      const codeMatch = item?.code?.toLowerCase()?.includes(term);

      return {
        ...item,
        display: nameMatch || codeMatch
      };
    });

    setClasses(updatedList);
  };

  const applyUsersDispersedSearch = (searchTerm = '', originalList = []) => {
    const term = searchTerm?.toLowerCase();

    const updatedList = originalList?.map((item) => {
      const codeMatch = item?.code?.toLowerCase()?.includes(term);
      const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
      const emailMatch = item?.email?.toLowerCase()?.includes(term);
      const nameMatch = fullName.includes(term);

      return {
        ...item,
        display: nameMatch || codeMatch || emailMatch
      };
    });

    setUsersList(updatedList);
  };

  const handleClassSearch = (e) => {
    const value = e?.target?.value ?? '';
    setClassInputSearch(value);
    applyClassDispersedSearch(value, classes);
  };

  const handleUserSearch = (e) => {
    const value = e.target.value;
    setUserInputSearch(value);
    applyUsersDispersedSearch(value, usersList);
  };

  const handleDeleteUserFromClass = (e, classCode, userCode) => {
    e.stopPropagation();
    e.preventDefault();

    const newClasses = classes?.map((item) => {
      if (item?.code === classCode) {
        const newReviewers = item?.reviewers?.filter((person) => person?.code !== userCode);
        return { ...item, reviewers: newReviewers };
      }
      return item;
    });

    setClasses(newClasses);
  };

  const onDragEnd = async (result) => {
    setIsDragging(false);

    const { source, destination } = result;
    if (
      !destination ||
      !source ||
      source.droppableId !== 'DRAGGABLE_USERS_LIST' ||
      destination.droppableId.indexOf('DROPPABLE_CLASS') === -1
    ) {
      return;
    }

    const destinationClass = destination.droppableId.match(/\[(.*?)\]/)?.[1];
    const foundUsers = usersList?.filter((item) => selectedUserIds?.includes(item?.code));
    const newClasses = classes?.map((item) => {
      if (item?.code === destinationClass) {
        const newReviewers = [...item?.reviewers, ...foundUsers].filter(
          (obj, index, self) => index === self.findIndex((o) => o.code === obj.code)
        );
        return { ...item, reviewers: newReviewers };
      }
      return item;
    });

    setClasses(newClasses);
  };

  const onDragStart = (result) => {
    setIsDragging(true);
  };

  const onConfirm = () => {
    dispatch(
      updateAssessmentsBuilderClass({
        selectedClasses: classes
      })
    );
    dispatch(updateFilter({ type: 'assessmentReviewers', open: false }));
    dispatch(updateFilter('assessmentReviewers', {}));
    setIsConfirming(false);
  };

  const filteredClasses = classes?.filter((item) => item?.display);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const retrieveUsers = async (filter = {}) => {
      setIsLoadingUsers(true);
      const isMaestroUser = userRoles.find((role) => role?.id === 'Maestro');
      const notAllowedRoles = ['Student', 'Parent'];

      if (!isMaestroUser) {
        notAllowedRoles.push('Maestro');
      }
      let customFilter = {
        ...filter,
        notInRolesId: notAllowedRoles
      };

      const { data = [] } = await getUsers(customFilter);

      const mappedUsers = data?.map((item) => {
        return { ...item, display: true };
      });
      setUsersList(mappedUsers);
      setIsLoadingUsers(false);
    };

    const { code, firstName, lastName, preferredName, email, rolesId = [] } = filter;

    if (
      filter &&
      Object.keys(filter).length > 0 &&
      !open &&
      (code || firstName || lastName || preferredName || email || rolesId?.length > 0)
    ) {
      retrieveUsers(filter);
    } else {
      setUsersList([]);
      setSelectedUserIds([]);
      setIsLoadingUsers(false);
    }
  }, [filter]);

  useEffect(() => {
    if (isConfirming) {
      onConfirm();
    }
  }, [isConfirming]);

  return (
    <Box _sx={{ height: '48rem', width: '100%' }}>
      <ReviewersPanelHeader
        handleClassSearch={handleClassSearch}
        classInputSearch={classInputSearch}
        handleUserSearch={handleUserSearch}
        userInputSearch={userInputSearch}
        users={usersList}
        selectedUserIds={selectedUserIds}
        setSelectedUserIds={setSelectedUserIds}
      />
      <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <Box style={{ height: '58vh', display: 'flex' }}>
          <Box
            sx={{
              width: '60%',
              position: 'relative',
              overflowY: 'auto',
              height: '100%',
              backgroundColor: '#fbfcfd',
              overflowX: 'hidden'
            }}
          >
            <Box sx={{ width: '100%' }} className='Xcontainer'>
              {isMounted &&
                filteredClasses?.length > 0 &&
                filteredClasses.map((classItem) => {
                  const { code } = classItem;
                  const classReviewers = classItem?.reviewers || [];
                  return (
                    <Box sx={{ width: '48%' }}>
                      <Droppable droppableId={'DROPPABLE_CLASS[' + classItem?.code + ']'}>
                        {(provided, snapshot) => {
                          return (
                            <div
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                              className={`reviewers-panel__droppable `}
                            >
                              <Card
                                sx={{
                                  m: 1,
                                  width: '48%',
                                  transition: 'transform 0.3s ease-in-out',
                                  boxShadow: 3,
                                  '&:hover': {
                                    transform: 'scale(1.02)',
                                    boxShadow: 6
                                  },
                                  position: 'relative'
                                }}
                                className={`${
                                  isDragging ? 'reviewers-panel__droppable--dragging-over' : ''
                                }  ${
                                  snapshot.isDraggingOver
                                    ? 'reviewers-panel__active-droppable--dragging-over'
                                    : ''
                                }`}
                              >
                                <CardHeader
                                  sx={{
                                    textAlign: 'center',
                                    backgroundColor: 'var(--primary)',
                                    color: '#fff',
                                    textTransform: 'uppercase'
                                  }}
                                  title={
                                    <div className='box-space-between text-medium w-100'>
                                      <Typography variant='h6'>{classItem?.code}</Typography>
                                    </div>
                                  }
                                  component='div'
                                />
                                <Divider />
                                <Box className='px-2 py-2'>
                                  {classReviewers?.length === 0 ? (
                                    <TextNotAssigned
                                      text='No reviewers assigned yet'
                                      size='md'
                                      className='py-4 mt-2'
                                    />
                                  ) : (
                                    <List dense={false}>
                                      {classReviewers?.map((item) => {
                                        const roles = item?.roles || [];

                                        return (
                                          <ListItem
                                            key={item?._id}
                                            secondaryAction={
                                              <IconButton
                                                edge='end'
                                                aria-label='delete'
                                                onClick={(e) =>
                                                  handleDeleteUserFromClass(e, code, item?.code)
                                                }
                                              >
                                                <DeleteIcon />
                                              </IconButton>
                                            }
                                          >
                                            <ListItemAvatar>
                                              <Avatar
                                                size='small'
                                                variant='circular'
                                                alt={item?.code}
                                                src={`photos/${item?.photo}`}
                                              />
                                            </ListItemAvatar>
                                            <ListItemText
                                              primary={`${item?.firstName} ${item?.lastName}`}
                                              secondary={
                                                <div
                                                  className='flex flex-wrap items-center gap-2 overflow-hidden'
                                                  style={{ maxWidth: '100%' }}
                                                >
                                                  <span style={{ fontSize: '10px' }}>{item?.code}</span>{' '}
                                                  {roles?.map((role) => {
                                                    const label =
                                                      typeof role === 'string' ? role : role?.name;
                                                    return (
                                                      <Chip
                                                        key={label}
                                                        label={label}
                                                        size='small'
                                                        sx={{ margin: '2px' }}
                                                      />
                                                    );
                                                  })}
                                                </div>
                                              }
                                            />
                                          </ListItem>
                                        );
                                      })}
                                    </List>
                                  )}
                                </Box>

                                <div className={`text-small drop-here__message `}>
                                  <Typography variant='caption' className='drop-here__message-text'>
                                    Drag and Drop users here to add reviewers
                                  </Typography>
                                </div>
                              </Card>
                              {provided.placeholder}
                            </div>
                          );
                        }}
                      </Droppable>
                    </Box>
                  );
                })}
            </Box>
          </Box>

          <Box
            sx={{
              width: '40%',
              position: 'relative',
              backgroundColor: '#fff'
            }}
          >
            <ReviewerUsersPanel
              users={usersList}
              selectedUserIds={selectedUserIds}
              setSelectedUserIds={setSelectedUserIds}
              isLoadingUsers={isLoadingUsers}
              userInputSearch={userInputSearch}
            />
          </Box>
        </Box>
      </DragDropContext>
    </Box>
  );
};

export default ReviewersPanel;
